import _ from 'lodash';
import { FormGroupChild, FormGroupChildren, IFormGroupChild } from './types/form-group-children';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { FormGroupValue } from './types/form-group-value';
import { FormBuildConfig } from './types/form-build-config';
import { GzFormFieldWebComponent } from '../formField';
import { GzHardCodedFormField } from '../formField/component/GzHardCodedFormField';
import { GzFormListWebComponent } from '../formList';

export class FormGroup implements IFormGroupChild {
  public disabled: boolean;
  public readonly: boolean;

  private validSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public valid$: Observable<boolean> = this.validSubject.pipe(distinctUntilChanged());

  private flatChildren: FormGroupChild[] = Object.values(this.children);
  private subscriptions: Subscription[] = [];

  constructor(private children: FormGroupChildren) {
    this.subscribeToChildrenEvents();
  }

  public static build(config: FormBuildConfig): FormGroup {
    const children = {};
    for (const fieldName of Object.keys(config)) {
      if (
        config[fieldName] instanceof FormGroup ||
        config[fieldName] instanceof GzFormFieldWebComponent ||
        config[fieldName] instanceof GzHardCodedFormField ||
        config[fieldName] instanceof GzFormListWebComponent
      ) {
        children[fieldName] = config[fieldName];
      } else {
        children[fieldName] = FormGroup.build(config[fieldName] as FormBuildConfig);
      }
    }

    return new FormGroup(children);
  }

  public getFormChild(path: string[]): IFormGroupChild | null {
    if (path.length === 0) {
      return null;
    }

    // check that the item exists in the form-field
    const childName = path[0];
    if (!this.children.hasOwnProperty(path[0])) {
      return null;
    }

    if (path.length === 1) {
      return this.children[childName];
    }

    return (this.children[childName] as FormGroup).getFormChild(path.slice(1));
  }

  public getFormChildren(): FormGroupChildren {
    return this.children;
  }

  public get valid(): boolean {
    return this.validSubject.value;
  }

  public get value(): FormGroupValue {
    return this.getValueRecursively(this);
  }

  public get detailedValid(): FormGroupValue {
    return this.getValidRecursively(this);
  }

  public set value(value: FormGroupValue) {
    for (const childName of Object.keys(value)) {
      if (!this.children.hasOwnProperty(childName)) {
        continue;
      }

      this.children[childName].value = value[childName];
    }
  }

  public get hasUnsavedChanges(): boolean {
    for (const child of this.flatChildren) {
      if (child.hasUnsavedChanges) {
        return true;
      }
    }

    return false;
  }

  public refreshInitialFormValue(): void {
    for (const child of this.flatChildren) {
      child.refreshInitialFormValue();
    }
  }

  public setChildrenAttribute(attribute: string, value: string) {
    for (const child of this.flatChildren) {
      child[attribute] = value;
    }
  }

  public validate(markAsTouched = false): void {
    for (const child of this.flatChildren) {
      child.validate(markAsTouched);
    }
  }

  public addFormChild(path: string[], child: FormGroupChild) {
    if (path.length === 0) {
      return;
    }

    if (path.length === 1) {
      // if it reached the last item in path, add the child
      this.children[path[0]] = child;
      child.validate();
      this.onUpdateChildren();
      return;
    }

    const childName = path[0];

    // create a form-group if the item does not exist
    if (!this.children.hasOwnProperty(childName)) {
      this.children[childName] = new FormGroup({});
      this.onUpdateChildren();
    }

    // if the item exists but is a form-field, then exit
    if (!(this.children[childName] instanceof FormGroup)) {
      throw new Error(`Expected form group during add form-child, found form-field`);
    }

    (this.children[childName] as FormGroup).addFormChild(path.slice(1), child);
  }

  public removeFormChild(path: string[]) {
    if (path.length === 0) {
      return;
    }

    // check that the item exists in the form-field
    const childName = path[0];
    if (!this.children.hasOwnProperty(childName)) {
      return;
    }

    if (path.length === 1) {
      this.children = _.omit(this.children, [childName]);
      this.onUpdateChildren();
      return;
    }

    (this.children[childName] as FormGroup).removeFormChild(path.slice(1));
  }

  public unsubscribe(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }

    this.subscriptions = [];
  }

  private onUpdateChildren() {
    // first unsubscribe from previous children
    this.unsubscribe();
    // get flat children
    this.flatChildren = Object.values(this.children);
    // subscribe to the new children
    this.subscribeToChildrenEvents();
    // and perform a validation
    this.validate();
  }

  public getValueRecursively(group: FormGroup, getDisableValues: boolean = false): FormGroupValue {
    const formValue: FormGroupValue = {};

    for (const key of Object.keys(group.children)) {
      const child = group.children[key];
      if (!getDisableValues && child.disabled) {
        continue;
      }

      formValue[key] = child instanceof FormGroup ? this.getValueRecursively(child, getDisableValues) : child.value;
    }

    return formValue;
  }

  private getValidRecursively(group: FormGroup): FormGroupValue {
    const formValid: FormGroupValue = {};

    for (const key of Object.keys(group.children)) {
      const child = group.children[key];

      formValid[key] = child instanceof FormGroup ? this.getValidRecursively(child) : child.valid;
    }

    return {
      selfValid: this.valid,
      children: formValid,
    };
  }

  public applyToFormGroupFields(method: (field: FormGroupChild) => void) {
    for (const child of this.flatChildren) {
      if (child instanceof FormGroup) {
        child.applyToFormGroupFields(method);
      } else {
        method(child);
      }
    }
  }

  public setDisabled(disabled: boolean) {
    this.applyToFormGroupFields(field => {
      field.disabled = disabled;
    });
  }

  private subscribeToChildrenEvents() {
    for (const child of this.flatChildren) {
      this.subscriptions.push(
        child.valid$.subscribe({
          next: () => {
            // when a child changes the validity, update self validity
            this.validSubject.next(this.isValid());
          },
        })
      );
    }
  }

  private isValid(): boolean {
    for (const child of this.flatChildren) {
      if (!child.valid && !child.disabled) {
        return false;
      }
    }

    return true;
  }

  public discardChanges(): void {
    for (const child of this.flatChildren) {
      child.discardChanges();
    }
  }
}
